import React, { useState, useEffect } from 'react';
import { Deal } from '../types/deal';
import './DealBanner.css';

interface DealBannerProps {
  deal: Deal;
}

const urunnLogo = `${process.env.PUBLIC_URL}/logo.svg`;

const DealBanner: React.FC<DealBannerProps> = ({ deal }) => {
  const [logoUrl, setLogoUrl] = useState<string>('');
  const [logoLoaded, setLogoLoaded] = useState<boolean>(false);

  useEffect(() => {
    const loadLogo = async () => {
      if (!deal.logo) {
        setLogoUrl('');
        return;
      }

      const cacheKey = `firebase_logo_${deal.logo}`;
      const cachedUrl = localStorage.getItem(cacheKey);

      if (cachedUrl) {
        setLogoUrl(cachedUrl);
        console.log('[DealBanner] Logo loaded from localStorage cache:', deal.logo);
        return;
      }

      // Check if logo is a Firebase Storage path or a full URL
      if (deal.logo.startsWith('gs://') || deal.logo.startsWith('https://firebasestorage') || !deal.logo.startsWith('http')) {
        try {
          // It's a Firebase Storage reference or path
          let storagePath = deal.logo;

          // If it's a gs:// reference, extract the path
          if (deal.logo.startsWith('gs://')) {
            // Format: gs://bucket-name/path/to/file
            storagePath = deal.logo.split('/').slice(3).join('/');
            console.log('[DealBanner] Converted gs:// reference to path:', storagePath);
          } else if (deal.logo.startsWith('https://firebasestorage')) {
            // Full HTTPS URL - try to extract the path, or use as-is with auth token
            try {
              const url = new URL(deal.logo);
              // Extract path from URL like /v0/b/bucket/o/path%2Fto%2Ffile
              const match = url.pathname.match(/\/o\/(.+)$/);
              if (match) {
                storagePath = decodeURIComponent(match[1]);
                console.log('[DealBanner] Extracted path from HTTPS URL:', storagePath);
              } else {
                // If we can't extract path, the URL should already have auth token
                setLogoUrl(deal.logo);
                localStorage.setItem(cacheKey, deal.logo);
                console.log('[DealBanner] Using Firebase Storage HTTPS URL with existing auth token');
                return;
              }
            } catch (e) {
              console.warn('[DealBanner] Error parsing HTTPS URL, using as-is:', e);
              setLogoUrl(deal.logo);
              localStorage.setItem(cacheKey, deal.logo);
              return;
            }
          }

          // Import Firebase Storage functions
          const { getStorage, ref, getDownloadURL } = await import('firebase/storage');
          const storage = getStorage();
          const fileRef = ref(storage, storagePath);
          
          // Get the signed download URL with proper auth token and alt=media
          console.log('[DealBanner] Fetching signed download URL for storage path:', storagePath);
          const url = await getDownloadURL(fileRef);
          console.log('[DealBanner] Got signed download URL:', url);
          localStorage.setItem(cacheKey, url);
          setLogoUrl(url);
          console.log('[DealBanner] Firebase Storage path converted to signed download URL with token:', storagePath);
        } catch (error) {
          console.error('[DealBanner] Error loading logo from Firebase Storage:', error);
          setLogoUrl('');
        }
      } else {
        // It's a regular HTTP/HTTPS URL
        setLogoUrl(deal.logo);
        localStorage.setItem(cacheKey, deal.logo);
      }
    };

    loadLogo();
  }, [deal.logo]);

  const align = deal.logoLayout?.align || 'center';
  const scale = (deal.logoLayout?.scale ?? 100) / 100;
  const justify = align === 'left' ? 'flex-start' : align === 'right' ? 'flex-end' : 'center';

  return (
    <div
      className="deal-banner"
      style={{ justifyContent: justify }}
    >
      <div
        className="deal-banner-logos"
        style={{ transform: scale !== 1 ? `scale(${scale})` : undefined, transformOrigin: align }}
      >
        <img
          src={urunnLogo}
          alt="Urunn"
          className="deal-banner-urunn-logo"
        />
        
        {logoUrl && (
          <div className="deal-banner-separator" style={{ borderColor: 'white' }}></div>
        )}

        {logoUrl && !logoLoaded && (
          <span className="deal-banner-partner-logo deal-banner-skeleton" aria-hidden="true" />
        )}

        {logoUrl && (
          <img
            src={logoUrl}
            alt={deal.name}
            className="deal-banner-partner-logo"
            style={{ display: logoLoaded ? undefined : 'none' }}
            onLoad={() => setLogoLoaded(true)}
            onError={() => {
              console.error('[DealBanner] Error loading logo image');
              setLogoUrl('');
            }}
          />
        )}
      </div>
    </div>
  );
};

export default DealBanner;
